import { ProjectTemplate, ProjectPhase, AgentConfig } from '../models/genericStudio';

// Agent configurations for different purposes
const ideationAgents: AgentConfig[] = [
  {
    agentType: 'abstract-thinker',
    agentName: 'Abstract Thinker',
    required: true,
    config: {
      depth: 'deep',
      temperature: 0.8,
    },
  },
  {
    agentType: 'prompt-improvement',
    agentName: 'Prompt Improvement',
    required: false,
    dependencies: ['abstract-thinker'],
    config: {
      iterations: 2,
    },
  },
];

const storyDevelopmentAgents: AgentConfig[] = [
  {
    agentType: 'story-generator',
    agentName: 'Story Generator',
    required: true,
    config: {
      mode: 'multi-agent',
    },
  },
  {
    agentType: 'character',
    agentName: 'Character Agent',
    required: true,
    parallel: true,
  },
  {
    agentType: 'scene',
    agentName: 'Scene Agent',
    required: true,
    dependencies: ['character'],
  },
];

const visualDevelopmentAgents: AgentConfig[] = [
  {
    agentType: 'image-prompt',
    agentName: 'Image Prompt Iterator',
    required: true,
  },
  {
    agentType: 'storyboard',
    agentName: 'Storyboard Agent',
    required: false,
    parallel: true,
  },
];

const technicalDevelopmentAgents: AgentConfig[] = [
  {
    agentType: 'incubation',
    agentName: 'Incubation Studio',
    required: true,
    config: {
      systemType: 'full-stack',
    },
  },
  {
    agentType: 'coding',
    agentName: 'AI Coding Assistant',
    required: true,
    dependencies: ['incubation'],
    config: {
      mode: 'multi-agent',
    },
  },
];

const productionAgents: AgentConfig[] = [
  {
    agentType: 'image-generation',
    agentName: 'Image Generation Studio',
    required: false,
    parallel: true,
    config: {
      quality: 'high',
      batchSize: 5,
    },
  },
  {
    agentType: 'content-pack',
    agentName: 'Content Pack Studio',
    required: false,
    parallel: true,
  },
];

const distributionAgents: AgentConfig[] = [
  {
    agentType: 'social-media',
    agentName: 'Social Media Campaign',
    required: true,
    config: {
      platforms: ['instagram', 'twitter', 'linkedin'],
    },
  },
  {
    agentType: 'blog',
    agentName: 'Blog Multi-Agent',
    required: false,
    parallel: true,
  },
  {
    agentType: 'tech-doc',
    agentName: 'Tech Documentation',
    required: false,
    dependencies: ['blog'],
  },
];

// Research agent configurations
const dataCollectionAgents: AgentConfig[] = [
  {
    agentType: 'ragtag-upload',
    agentName: 'Document Upload',
    required: false,
    config: {
      autoTag: true,
    },
  },
  {
    agentType: 'data-analysis-rss',
    agentName: 'RSS Feed Analyzer',
    required: false,
    parallel: true,
  },
  {
    agentType: 'data-analysis-csv',
    agentName: 'CSV Data Analyzer',
    required: false,
    parallel: true,
  },
  {
    agentType: 'image-analysis',
    agentName: 'Image Analysis AI',
    required: false,
    parallel: true,
  },
];

const researchAnalysisAgents: AgentConfig[] = [
  {
    agentType: 'ragtag-query',
    agentName: 'Document Query Engine',
    required: true,
    config: {
      maxContextTokens: 4000,
      maxResponseTokens: 1000,
    },
  },
  {
    agentType: 'simple-data-query',
    agentName: 'Data Query Agent',
    required: false,
    parallel: true,
  },
  {
    agentType: 'ragtag-summarize',
    agentName: 'Document Summarizer',
    required: true,
    config: {
      summaryType: 'detailed',
    },
  },
  {
    agentType: 'simple-data-summarize',
    agentName: 'Data Summarizer',
    required: false,
    parallel: true,
  },
];

const researchSynthesisAgents: AgentConfig[] = [
  {
    agentType: 'abstract-thinking',
    agentName: 'Abstract Thinker',
    required: true,
    config: {
      depth: 'deep',
      iterations: 3,
      temperature: 0.8,
    },
  },
  {
    agentType: 'insight-synthesis',
    agentName: 'Insight Synthesizer',
    required: true,
    dependencies: ['abstract-thinking'],
  },
];

const researchReportingAgents: AgentConfig[] = [
  {
    agentType: 'report-generation',
    agentName: 'Report Generator',
    required: true,
    config: {
      reportType: 'technical',
      includeVisualizations: true,
    },
  },
];

// Cuisine agent configurations
const cuisineIdeationAgents: AgentConfig[] = [
  {
    agentType: 'abstract-thinker',
    agentName: 'Culinary Concept Explorer',
    required: true,
    config: {
      depth: 'deep',
      temperature: 0.8,
      focus: 'culinary concepts and food experiences',
    },
  },
];

const cuisineDevelopmentAgents: AgentConfig[] = [
  {
    agentType: 'cuisine-italian',
    agentName: 'Italian Cuisine Expert',
    required: false,
    config: {
      includeTraditionalElements: true,
      emphasizeAuthenticity: true,
    },
  },
  {
    agentType: 'cuisine-mexican',
    agentName: 'Mexican Cuisine Expert',
    required: false,
    config: {
      includeUNESCOElements: true,
      includePrehispanicElements: true,
    },
  },
  {
    agentType: 'cuisine-indian',
    agentName: 'Indian Cuisine Expert',
    required: false,
    config: {
      includeAyurvedicPrinciples: true,
      dietaryTradition: 'vegetarian',
    },
  },
  {
    agentType: 'cuisine-thai',
    agentName: 'Thai Cuisine Expert',
    required: false,
    config: {
      includeTraditionalElements: true,
    },
  },
  {
    agentType: 'cuisine-japanese',
    agentName: 'Japanese Cuisine Expert',
    required: false,
    config: {
      emphasizeSeasonality: true,
      includeWashokuPrinciples: true,
    },
  },
  {
    agentType: 'cuisine-french',
    agentName: 'French Cuisine Expert',
    required: false,
    config: {
      classicalTechniques: true,
      includeWinePairings: true,
    },
  },
];

const cuisineProductionAgents: AgentConfig[] = [
  {
    agentType: 'image-prompt',
    agentName: 'Food Photography Prompt Creator',
    required: false,
    config: {
      style: 'food photography',
      focus: 'appetizing presentation',
    },
  },
  {
    agentType: 'image-generation',
    agentName: 'Food Image Generator',
    required: false,
    dependencies: ['image-prompt'],
    config: {
      quality: 'high',
      style: 'photorealistic food',
    },
  },
  {
    agentType: 'content-marketing',
    agentName: 'Recipe Marketing Content',
    required: false,
    config: {
      contentType: 'recipe blog',
    },
  },
];

// Project Templates
export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'story-project',
    name: 'Complete Story Project',
    description: 'Create a full story with characters, scenes, and visual elements',
    type: 'story',
    icon: '📚',
    estimatedTime: '2-3 hours',
    difficulty: 'intermediate',
    tags: ['creative writing', 'narrative', 'characters'],
    phases: [
      {
        id: 'ideation',
        name: 'Ideation & Concept Development',
        description: 'Explore and refine your story concept',
        agents: ideationAgents,
        status: 'pending',
      },
      {
        id: 'development',
        name: 'Story Development',
        description: 'Create characters, plot, and scenes',
        agents: storyDevelopmentAgents,
        status: 'pending',
      },
      {
        id: 'visual',
        name: 'Visual Development',
        description: 'Create visual representations and storyboards',
        agents: visualDevelopmentAgents,
        status: 'pending',
      },
      {
        id: 'production',
        name: 'Asset Production',
        description: 'Generate final story assets and visuals',
        agents: [
          ...productionAgents,
          {
            agentType: 'screenplay',
            agentName: 'Screenplay Generator',
            required: false,
          },
        ],
        status: 'pending',
      },
      {
        id: 'distribution',
        name: 'Publishing & Marketing',
        description: 'Prepare for distribution and promotion',
        agents: distributionAgents,
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'story',
      goals: ['Create engaging narrative', 'Develop memorable characters', 'Build immersive world'],
      tone: 'balanced',
      audience: 'general',
    },
  },
  {
    id: 'visual-art-project',
    name: 'Visual Art Collection',
    description: 'Create a cohesive collection of visual artworks',
    type: 'visual-art',
    icon: '🎨',
    estimatedTime: '1-2 hours',
    difficulty: 'beginner',
    tags: ['visual art', 'design', 'imagery'],
    phases: [
      {
        id: 'ideation',
        name: 'Concept & Theme Development',
        description: 'Define your artistic vision and theme',
        agents: ideationAgents,
        status: 'pending',
      },
      {
        id: 'prompt-development',
        name: 'Prompt Creation',
        description: 'Develop and refine image prompts',
        agents: [
          {
            agentType: 'image-prompt',
            agentName: 'Image Prompt Iterator',
            required: true,
            config: {
              iterations: 5,
              style: 'artistic',
            },
          },
          {
            agentType: 'urban-culture',
            agentName: 'Urban Culture Studio',
            required: false,
            parallel: true,
          },
        ],
        status: 'pending',
      },
      {
        id: 'production',
        name: 'Image Generation',
        description: 'Generate your art collection',
        agents: [
          {
            agentType: 'image-generation',
            agentName: 'Image Generation Studio',
            required: true,
            config: {
              providers: ['flux', 'luma'],
              variations: 3,
            },
          },
          {
            agentType: 'image-analysis',
            agentName: 'Image Analysis AI',
            required: false,
            dependencies: ['image-generation'],
          },
        ],
        status: 'pending',
      },
      {
        id: 'curation',
        name: 'Curation & Packaging',
        description: 'Organize and package your collection',
        agents: [
          {
            agentType: 'content-pack',
            agentName: 'Content Pack Studio',
            required: true,
            config: {
              packType: 'image',
            },
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'visual-art',
      goals: ['Create cohesive collection', 'Explore artistic theme', 'Generate portfolio'],
      style: 'contemporary',
    },
  },
  {
    id: 'technical-project',
    name: 'Full-Stack Application',
    description: 'Design and develop a complete software application',
    type: 'technical',
    icon: '💻',
    estimatedTime: '3-4 hours',
    difficulty: 'advanced',
    tags: ['programming', 'software', 'development'],
    phases: [
      {
        id: 'planning',
        name: 'System Design & Architecture',
        description: 'Plan your application architecture',
        agents: [
          ...ideationAgents,
          {
            agentType: 'ux-designer',
            agentName: 'UI/UX Designer',
            required: false,
            parallel: true,
          },
        ],
        status: 'pending',
      },
      {
        id: 'development',
        name: 'Implementation',
        description: 'Build the application',
        agents: technicalDevelopmentAgents,
        status: 'pending',
      },
      {
        id: 'testing',
        name: 'Testing & Optimization',
        description: 'Test and optimize your code',
        agents: [
          {
            agentType: 'coding',
            agentName: 'AI Coding Assistant',
            required: true,
            config: {
              task: 'testing',
              coverage: 'comprehensive',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'documentation',
        name: 'Documentation',
        description: 'Create comprehensive documentation',
        agents: [
          {
            agentType: 'tech-doc',
            agentName: 'Tech Documentation',
            required: true,
            config: {
              docTypes: ['api', 'user-guide', 'architecture'],
            },
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'technical',
      goals: ['Build scalable solution', 'Implement best practices', 'Create documentation'],
    },
  },
  {
    id: 'marketing-campaign',
    name: 'Marketing Campaign',
    description: 'Create a complete marketing campaign with content and visuals',
    type: 'marketing',
    icon: '📢',
    estimatedTime: '2-3 hours',
    difficulty: 'intermediate',
    tags: ['marketing', 'social media', 'content'],
    phases: [
      {
        id: 'strategy',
        name: 'Campaign Strategy',
        description: 'Define campaign goals and strategy',
        agents: [
          {
            agentType: 'abstract-thinker',
            agentName: 'Abstract Thinker',
            required: true,
            config: {
              focus: 'marketing strategy',
            },
          },
          {
            agentType: 'content-marketing',
            agentName: 'Content Marketing Agent',
            required: true,
          },
        ],
        status: 'pending',
      },
      {
        id: 'content-creation',
        name: 'Content Development',
        description: 'Create campaign content',
        agents: [
          {
            agentType: 'social-media',
            agentName: 'Social Media Campaign',
            required: true,
          },
          {
            agentType: 'blog',
            agentName: 'Blog Multi-Agent',
            required: false,
            parallel: true,
          },
          {
            agentType: 'journalism',
            agentName: 'Journalism Multi-Agent',
            required: false,
            parallel: true,
          },
        ],
        status: 'pending',
      },
      {
        id: 'visual-assets',
        name: 'Visual Asset Creation',
        description: 'Generate campaign visuals',
        agents: [
          ...visualDevelopmentAgents,
          {
            agentType: 'fashion',
            agentName: 'Fashion Multi-Agent',
            required: false,
            config: {
              focus: 'brand aesthetics',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'packaging',
        name: 'Campaign Packaging',
        description: 'Package and prepare for launch',
        agents: productionAgents,
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'marketing',
      goals: ['Increase brand awareness', 'Drive engagement', 'Generate leads'],
      audience: 'target demographic',
    },
  },
  {
    id: 'educational-content',
    name: 'Educational Course',
    description: 'Create an interactive educational experience',
    type: 'educational',
    icon: '🎓',
    estimatedTime: '3-4 hours',
    difficulty: 'advanced',
    tags: ['education', 'learning', 'interactive'],
    phases: [
      {
        id: 'curriculum',
        name: 'Curriculum Design',
        description: 'Plan your educational content',
        agents: [
          ...ideationAgents,
          {
            agentType: 'scientific',
            agentName: 'Scientific Agent Explorer',
            required: false,
            config: {
              domain: 'education',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'content',
        name: 'Content Creation',
        description: 'Develop educational materials',
        agents: [
          {
            agentType: 'interactive-fiction',
            agentName: 'Interactive Fiction Agent',
            required: true,
            config: {
              format: 'educational',
            },
          },
          {
            agentType: 'novel-writing',
            agentName: 'Novel Writing Assistant',
            required: false,
            config: {
              format: 'textbook',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'assessment',
        name: 'Assessment Design',
        description: 'Create quizzes and assessments',
        agents: [
          {
            agentType: 'simple-data',
            agentName: 'Simple Data Agent',
            required: true,
            config: {
              task: 'faq-generation',
            },
          },
          {
            agentType: 'data-analysis',
            agentName: 'Data Analysis Studio',
            required: false,
          },
        ],
        status: 'pending',
      },
      {
        id: 'multimedia',
        name: 'Multimedia Enhancement',
        description: 'Add visual and interactive elements',
        agents: [
          ...visualDevelopmentAgents,
          {
            agentType: 'video-prompt',
            agentName: 'Video Prompt Iterator',
            required: false,
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'educational',
      goals: ['Educate effectively', 'Engage learners', 'Assess understanding'],
      audience: 'students',
    },
  },
  {
    id: 'market-research',
    name: 'Market Research Project',
    description: 'Comprehensive market analysis with data from multiple sources',
    type: 'market-research',
    icon: '📊',
    estimatedTime: '2-3 hours',
    difficulty: 'intermediate',
    tags: ['research', 'market-analysis', 'data-driven'],
    phases: [
      {
        id: 'data-collection',
        name: 'Data Collection',
        description: 'Gather data from RSS feeds, documents, and other sources',
        agents: dataCollectionAgents,
        status: 'pending',
      },
      {
        id: 'analysis',
        name: 'Data Analysis',
        description: 'Analyze collected data for insights',
        agents: researchAnalysisAgents,
        status: 'pending',
      },
      {
        id: 'synthesis',
        name: 'Insight Synthesis',
        description: 'Synthesize findings and identify patterns',
        agents: researchSynthesisAgents,
        status: 'pending',
      },
      {
        id: 'reporting',
        name: 'Report Generation',
        description: 'Create comprehensive research report',
        agents: researchReportingAgents,
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'research-report',
      goals: ['Identify market trends', 'Analyze competition', 'Generate actionable insights'],
    },
  },
  {
    id: 'competitive-intelligence',
    name: 'Competitive Intelligence',
    description: 'Deep analysis of competitors and industry landscape',
    type: 'competitive-intelligence',
    icon: '🔍',
    estimatedTime: '3-4 hours',
    difficulty: 'advanced',
    tags: ['research', 'competitive-analysis', 'strategic'],
    phases: [
      {
        id: 'data-collection',
        name: 'Intelligence Gathering',
        description: 'Collect data on competitors and industry',
        agents: [
          ...dataCollectionAgents,
          {
            agentType: 'journalism',
            agentName: 'Journalism Multi-Agent',
            required: false,
            config: {
              articleType: 'investigative',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'analysis',
        name: 'Competitive Analysis',
        description: 'Analyze competitor strategies and positioning',
        agents: researchAnalysisAgents,
        status: 'pending',
      },
      {
        id: 'synthesis',
        name: 'Strategic Insights',
        description: 'Generate strategic recommendations',
        agents: researchSynthesisAgents,
        status: 'pending',
      },
      {
        id: 'reporting',
        name: 'Intelligence Report',
        description: 'Create executive intelligence briefing',
        agents: [
          ...researchReportingAgents,
          {
            agentType: 'report-generation',
            agentName: 'Executive Report',
            required: true,
            config: {
              reportType: 'executive',
              confidential: true,
            },
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'research-report',
      goals: ['Understand competitor strategies', 'Identify market opportunities', 'Inform strategic decisions'],
    },
  },
  {
    id: 'scientific-research',
    name: 'Scientific Research Study',
    description: 'Systematic research with data analysis and peer review',
    type: 'research-report',
    icon: '🔬',
    estimatedTime: '4-5 hours',
    difficulty: 'advanced',
    tags: ['research', 'scientific', 'academic'],
    phases: [
      {
        id: 'literature-review',
        name: 'Literature Review',
        description: 'Review existing research and publications',
        agents: [
          {
            agentType: 'ragtag-upload',
            agentName: 'Research Papers Upload',
            required: true,
            config: {
              autoTag: true,
              tagPrefix: 'literature',
            },
          },
          {
            agentType: 'ragtag-summarize',
            agentName: 'Literature Summarizer',
            required: true,
            config: {
              summaryType: 'academic',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'data-collection',
        name: 'Data Collection',
        description: 'Gather experimental or observational data',
        agents: [
          {
            agentType: 'data-analysis-csv',
            agentName: 'Experimental Data Analysis',
            required: true,
          },
          {
            agentType: 'simple-data-query',
            agentName: 'Data Validation',
            required: true,
          },
        ],
        status: 'pending',
      },
      {
        id: 'analysis',
        name: 'Statistical Analysis',
        description: 'Perform rigorous data analysis',
        agents: [
          ...researchAnalysisAgents,
          {
            agentType: 'scientific',
            agentName: 'Scientific Agent Explorer',
            required: true,
            config: {
              domain: 'data-science',
              rigor: 'high',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'synthesis',
        name: 'Research Synthesis',
        description: 'Synthesize findings and draw conclusions',
        agents: researchSynthesisAgents,
        status: 'pending',
      },
      {
        id: 'reporting',
        name: 'Paper Generation',
        description: 'Create research paper with citations',
        agents: [
          {
            agentType: 'report-generation',
            agentName: 'Academic Paper Generator',
            required: true,
            config: {
              reportType: 'academic',
              includeCitations: true,
              includeMethodology: true,
            },
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'research-report',
      goals: ['Advance scientific knowledge', 'Test hypotheses', 'Publish findings'],
    },
  },
  {
    id: 'culinary-project',
    name: 'Culinary Experience Design',
    description: 'Create comprehensive culinary experiences with authentic recipes and cultural context',
    type: 'culinary',
    icon: '👨‍🍳',
    estimatedTime: '1-2 hours',
    difficulty: 'intermediate',
    tags: ['cuisine', 'recipes', 'food culture', 'cooking'],
    phases: [
      {
        id: 'culinary-ideation',
        name: 'Culinary Concept Development',
        description: 'Define the culinary theme, cuisine style, and dining experience',
        agents: cuisineIdeationAgents,
        status: 'pending',
      },
      {
        id: 'recipe-development',
        name: 'Recipe Creation',
        description: 'Develop authentic recipes with cultural context and techniques',
        agents: cuisineDevelopmentAgents,
        status: 'pending',
      },
      {
        id: 'visual-production',
        name: 'Visual & Marketing',
        description: 'Create appetizing visuals and marketing content',
        agents: cuisineProductionAgents,
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'culinary-experience',
      goals: ['Create authentic recipes', 'Preserve cultural heritage', 'Inspire home cooking'],
    },
  },
  {
    id: 'restaurant-concept',
    name: 'Restaurant Concept Development',
    description: 'Design a complete restaurant concept with menu, branding, and marketing',
    type: 'culinary',
    icon: '🍽️',
    estimatedTime: '3-4 hours',
    difficulty: 'advanced',
    tags: ['restaurant', 'menu design', 'branding', 'hospitality'],
    phases: [
      {
        id: 'concept',
        name: 'Restaurant Concept',
        description: 'Define restaurant theme, target audience, and unique value proposition',
        agents: [
          ...ideationAgents,
          {
            agentType: 'content-marketing',
            agentName: 'Brand Strategist',
            required: true,
            config: {
              focus: 'restaurant branding',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'menu-development',
        name: 'Menu Development',
        description: 'Create signature dishes across multiple cuisines',
        agents: [
          ...cuisineDevelopmentAgents,
          {
            agentType: 'cuisine-general',
            agentName: 'General Culinary Consultant',
            required: true,
            config: {
              focus: 'menu harmony and balance',
            },
          },
        ],
        status: 'pending',
      },
      {
        id: 'marketing',
        name: 'Marketing & Launch',
        description: 'Create marketing materials and launch strategy',
        agents: [
          ...cuisineProductionAgents,
          {
            agentType: 'social-media',
            agentName: 'Restaurant Social Media',
            required: true,
            config: {
              platforms: ['instagram', 'facebook', 'tiktok'],
              focus: 'food and dining experience',
            },
          },
        ],
        status: 'pending',
      },
    ],
    defaultConfig: {
      projectType: 'restaurant-business',
      goals: ['Create unique dining experience', 'Build strong brand', 'Attract target customers'],
    },
  },
];

// Helper function to get template by ID
export const getProjectTemplate = (templateId: string): ProjectTemplate | undefined => {
  return PROJECT_TEMPLATES.find(t => t.id === templateId);
};

// Helper function to get templates by type
export const getTemplatesByType = (type: string): ProjectTemplate[] => {
  return PROJECT_TEMPLATES.filter(t => t.type === type);
};